/**
 * /demo — Norway-first (or any-country) demo deep link.
 *
 * Usage:
 *   https://app.keikka.work/demo?country=NO
 *   https://app.keikka.work/demo?country=NO&lang=nb
 *   https://app.keikka.work/demo?country=FI&lang=fi
 *   https://app.keikka.work/demo?country=EE&lang=et
 *
 * Behaviour:
 *   1. Reads `country`, `lang`, `dismissGeo` from the querystring.
 *   2. Picks a sensible default UI language if `lang` is missing:
 *        NO -> nb, FI -> fi, EE -> et, * -> en
 *   3. Writes prefs + language storage keys SYNCHRONOUSLY before first
 *      paint:
 *        - `keikka_prefs_v1`         = {uiLang, workCountry, serviceCountry}
 *        - `keikka_lang`             = <lang>  (read by the i18n manager)
 *        - `keikka_geo_dismissed_at` = now()   (suppresses the first-visit
 *                                               location banner)
 *   4. Replaces history to `/` so refresh lands on the real home with the
 *      seeded prefs intact and no `/demo` in the URL bar.
 */
import React, { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/lib/context';

const COUNTRY_DEFAULT_LANG = {
  NO: 'nb',
  FI: 'fi',
  EE: 'et',
  SE: 'sv',
  RU: 'ru',
};

const SUPPORTED_LANGS = new Set(['et', 'en', 'fi', 'nb', 'ru', 'sv']);
const SUPPORTED_COUNTRIES = new Set(['EE', 'FI', 'NO', 'SE', 'RU', 'LV', 'LT', 'DK', 'DE', 'PL']);

// Module-level seed: runs once, synchronously, on the first import of this
// file — i.e. the moment React Router resolves `/demo`. Writing storage
// here means LanguageProvider's initial `useState(() => initializeI18n())`
// on the next route tick already sees the desired locale.
function seedFromLocation() {
  try {
    const params = new URLSearchParams(window.location.search);
    const rawCountry = (params.get('country') || '').toUpperCase().trim();
    if (!rawCountry || !SUPPORTED_COUNTRIES.has(rawCountry)) return null;

    const rawLang = (params.get('lang') || '').toLowerCase().trim();
    const lang = SUPPORTED_LANGS.has(rawLang)
      ? rawLang
      : (COUNTRY_DEFAULT_LANG[rawCountry] || 'en');

    const dismissGeo = params.get('dismissGeo') !== '0';

    const prefs = {
      uiLang: lang,
      workCountry: rawCountry,
      serviceCountry: rawCountry,
    };
    localStorage.setItem('keikka_prefs_v1', JSON.stringify(prefs));
    localStorage.setItem('keikka_lang', lang);
    // Mark this session as Investor-Demo so the live map merges in seeded
    // demo providers (each clearly labelled is_demo / DEMO badge).
    localStorage.setItem('keikka_demo', 'true');
    if (dismissGeo) {
      localStorage.setItem('keikka_geo_dismissed_at', String(Date.now()));
    }
    return { lang, country: rawCountry };
  } catch (_) {
    return null;
  }
}

// Fire once on module load so the seed beats LanguageProvider's init hook.
const SEED = typeof window !== 'undefined' ? seedFromLocation() : null;

// Privacy-safe device fingerprint.
// Input: a tab-scoped UUID (sessionStorage, NEVER localStorage) + lang +
// screen size + timezone offset + raw UA. Output: SHA-256 hex.
//
// Guarantees:
//  - Cleared the moment the tab/window closes (sessionStorage).
//  - Not reversible — we hash before the network ever sees the source.
//  - Not transferable across incognito windows, other browsers, or devices.
//  - The server NEVER persists the raw UA or IP for demo_open events
//    that carry an `fp` (see backend routes/map_router.py).
async function buildDeviceFingerprint() {
  try {
    const SS_KEY = 'keikka_demo_session_v1';
    let sid = null;
    try { sid = sessionStorage.getItem(SS_KEY); } catch (_) {}
    if (!sid) {
      sid = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      try { sessionStorage.setItem(SS_KEY, sid); } catch (_) {}
    }
    const ua = (navigator.userAgent || '').slice(0, 256);
    const lang = (navigator.language || '').slice(0, 16);
    const w = (window.screen && window.screen.width) || 0;
    const h = (window.screen && window.screen.height) || 0;
    let tz = 0;
    try { tz = new Date().getTimezoneOffset(); } catch (_) {}
    const source = `${sid}|${lang}|${w}x${h}|${tz}|${ua}`;
    if (!(crypto && crypto.subtle && crypto.subtle.digest)) return null;
    const buf = new TextEncoder().encode(source);
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (_) {
    return null;
  }
}

// Fire-and-forget analytics beacon so the admin dashboard can see which
// market QR was scanned, on what device, and when. No new infra — reuses
// the existing /api/map/analytics endpoint.
async function sendDemoOpenBeacon(seed) {
  if (!seed || typeof window === 'undefined') return;
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    const ua = navigator.userAgent || '';
    const deviceType = /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'mobile' : 'desktop';
    const fp = await buildDeviceFingerprint();
    const body = JSON.stringify({
      event_type: 'demo_open',
      path: window.location.pathname + window.location.search,
      label: `country=${seed.country};lang=${seed.lang};device=${deviceType}`,
      session_id: `demo-${seed.country}-${Date.now()}`,
      device: deviceType,
      fp,
    });
    const url = `${base}/api/map/analytics/event`;
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      // Best-effort fallback
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true })
        .catch(() => {});
    }
  } catch (_) {
    // never block the redirect on analytics
  }
}
if (SEED) sendDemoOpenBeacon(SEED);

const DemoSeedRedirect = () => {
  const [params] = useSearchParams();
  const { setLanguage } = useLanguage();

  useEffect(() => {
    // LanguageProvider may have already mounted with the pre-demo language.
    // Force it to pick up the seeded value right away.
    if (SEED?.lang) {
      try { setLanguage(SEED.lang); } catch (_) {}
    }
    // Preserve optional `path` target so we can deep-link to /map or /jobs too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const target = params.get('to') || '/';

  return (
    <div
      data-testid="demo-seed-redirect"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        color: '#64748b',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 14,
      }}
    >
      <Navigate to={target} replace />
      Loading demo…
    </div>
  );
};

export default DemoSeedRedirect;
export { DemoSeedRedirect };
